import type { ReactNode } from 'react';
import { PortalChatMessage } from 'types/aiChat';
import { hasMeetingPrepMarker } from '../utils/meetingPrepPdfUtils';
import { extractToolCallHistory } from '../utils/toolCallParts';
import { BriefFigure } from './message/figure/BriefFigure';

interface Props {
    message: PortalChatMessage;
    isStreaming?: boolean;
}

/** Whether the answer shows its brief as a `showFigure` brief, which the answer's blocks draw where it anchors it. */
const hasBriefFigure = (message: PortalChatMessage): boolean =>
    extractToolCallHistory(message.parts).some(({ name, status, input, output }) => {
        const definition = output ?? input;
        return (
            name === 'showFigure' &&
            status !== 'error' &&
            typeof definition === 'object' &&
            definition !== null &&
            (definition as { kind?: unknown }).kind === 'brief'
        );
    });

/**
 * The brief Figure of a meeting-prep answer that shows no `showFigure` brief of its own, as answers written before
 * Figures do; nothing for any other message. It fills in while the answer streams, and an answer that ended with no
 * id, which a failed stream leaves, has no stored brief to show.
 */
export default function MeetingPrepActions({ message, isStreaming }: Props): ReactNode {
    if (
        message.role !== 'assistant' ||
        !hasMeetingPrepMarker(message) ||
        hasBriefFigure(message) ||
        (!isStreaming && typeof message.metadata?.aiChatMessageId !== 'number')
    ) {
        return null;
    }
    return <BriefFigure message={message} />;
}
